import { useState, useRef, useEffect, useCallback } from "react";
import { useAutocomplete } from "../hooks/useAutocomplete";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { STARTUP_API_URL } from "../apiConfig";
import Navbar from "../components/navbar";
import Footer from "../components/Footer";
import RippleWaveLoader from "../components/ui/RippleWaveLoader";
import { Alert, AlertIcon, AlertTitle } from "../components/ui/alert";
import { RiCheckboxCircleFill } from "@remixicon/react";
import { FiExternalLink, FiGlobe, FiDollarSign, FiCheckCircle, FiUsers, FiBookmark } from "react-icons/fi";
import { ChevronDownIcon } from "@heroicons/react/24/solid";

interface Startup {
  id: number | null;
  name: string;
  short_description: string;
  website: string | null;
  logo_url: string | null;
  founded_year: number | null;
  headquarters: string;
  funding_stage: string;
  total_funding: number;
  category: { id: number | null; name: string } | null;
  region: { country: string; state: string; city: string } | null;
  tags: string[];
  data_source: string;
  founders?: string[];
  source_urls?: string[];
  recent_funding?: string;
  investors?: string[];
  customer_engagements?: number;
  has_deployment_record?: boolean;
  confidence_score?: number;
  website_content?: string;
}

interface FilterOptions {
  categories: { id: number; name: string }[];
  countries: string[];
  funding_stages: string[];
}

type FilterMode = "all" | "database" | "live";

const LOAD_MORE_BATCH = 6;
const SS_KEY = "startupSearchState";

// Map Startup Directory category names → Engagement Repo sector dropdown values
const mapCategoryToSector = (categoryName: string): string => {
  const n = (categoryName || "").toLowerCase();
  if (n.includes("ai") || n.includes("machine learning") || n.includes("artificial intelligence") || n.includes("generative")) return "AI/ML";
  if (n.includes("fintech") || n.includes("finance") || n.includes("financial") || n.includes("payment") || n.includes("banking")) return "Fintech";
  if (n.includes("health") || n.includes("medtech") || n.includes("biotech") || n.includes("medical") || n.includes("pharma")) return "Healthcare";
  if (n.includes("saas") || n.includes("software as a service")) return "SaaS";
  if (n.includes("edtech") || n.includes("education") || n.includes("learning")) return "EdTech";
  if (n.includes("clean") || n.includes("energy") || n.includes("climate") || n.includes("sustainability") || n.includes("greentech")) return "CleanTech";
  if (n.includes("cyber") || n.includes("security") || n.includes("quantum")) return "Cybersecurity";
  if (n.includes("ecommerce") || n.includes("e-commerce") || n.includes("retail") || n.includes("commerce")) return "E-commerce";
  if (n.includes("iot") || n.includes("internet of things") || n.includes("hardware")) return "IoT";
  if (n.includes("blockchain") || n.includes("crypto") || n.includes("web3") || n.includes("defi")) return "Blockchain";
  return "";
};

const saveState = (data: Record<string, any>) => {
  try { sessionStorage.setItem(SS_KEY, JSON.stringify(data)); } catch {}
};
const loadState = (): Record<string, any> | null => {
  try { const v = sessionStorage.getItem(SS_KEY); return v ? JSON.parse(v) : null; } catch { return null; }
};

const formatFunding = (amount: number) => {
  if (!amount) return "Undisclosed";
  if (amount >= 1e9) return `$${(amount / 1e9).toFixed(1)}B`;
  if (amount >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
  if (amount >= 1e3) return `$${(amount / 1e3).toFixed(0)}K`;
  return `$${amount}`;
};

const formatFundingStage = (stage: string) => {
  const m: Record<string, string> = {
    pre_seed: "Pre-Seed", seed: "Seed",
    series_a: "Series A", series_b: "Series B",
    series_c: "Series C", series_d_plus: "Series D+",
    ipo: "IPO", acquired: "Acquired", unknown: "Unknown",
  };
  return m[stage] || stage;
};

const removeDuplicatesByName = (items: Startup[]) => {
  const seen = new Set<string>();
  return items.filter(s => {
    const key = s.name?.toLowerCase().trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Merges incoming DB/live results into existing list, preserving order.
// Prefill items (id=null) are REPLACED by their enriched DB/live counterparts.
// DB results also update the data_source badge on name-matched prefill cards.
const smartMerge = (prev: Startup[], incoming: Startup[]): Startup[] => {
  const inMap = new Map(incoming.map(s => [(s.name || "").toLowerCase(), s]));
  const isDbIncoming = incoming.some(s => s.data_source === "database");
  const merged = prev.map(s => {
    const key = (s.name || "").toLowerCase();
    if (!inMap.has(key)) return s;
    const match = inMap.get(key)!;
    // Replace prefill (id=null) entirely with enriched version
    if (s.id === null) return match;
    // For prefill cards already assigned a source, upgrade to "database" if DB confirms it
    if (isDbIncoming && match.data_source === "database") return { ...s, data_source: "database", id: match.id ?? s.id };
    return s;
  });
  const mergedNames = new Set(merged.map(s => (s.name || "").toLowerCase()));
  return [...merged, ...incoming.filter(s => !mergedNames.has((s.name || "").toLowerCase()))];
};

const StartupSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // If navigating from chat with prefill, ignore any stale session cache
  const hasPrefill = !!(location.state?.prefill_results?.length);
  // Show "Back to Chat" if session restore key exists in sessionStorage (persists through detail page round-trips)
  const showBackToChat = hasPrefill || !!sessionStorage.getItem("ucdp_return_session_id");
  const cached = useRef(hasPrefill ? null : loadState());
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // Tracked startup names — loaded from Engagement Repo for "Already Tracking" badge
  const [trackedNames, setTrackedNames] = useState<Set<string>>(new Set());
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    axios.get(`${STARTUP_API_URL}/api/v1/engagements/`, {
      headers: { Authorization: token },
      params: { limit: 500 },
    }).then(res => {
      if (res.data.status === "success") {
        const names = new Set<string>(
          (res.data.data as { company_name: string }[]).map(e => e.company_name.toLowerCase().trim())
        );
        setTrackedNames(names);
      }
    }).catch(() => {});
  }, []);

  // Results
  const [startups, setStartups] = useState<Startup[]>(cached.current?.startups || []);
  const [loading, setLoading] = useState(false);
  const [liveLoading, setLiveLoading] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [noMoreResults, setNoMoreResults] = useState(false);
  const autoMinFetchedRef = useRef(false); // prevent repeated auto-fetch per search
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Filter options from backend
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);

  // Text search input
  const [textQuery, setTextQuery] = useState(cached.current?.textQuery || "");
  const ac = useAutocomplete(textQuery);
  const triggerSearch = useCallback(() => { (document.getElementById("startup-search-btn") as HTMLButtonElement)?.click(); }, []);

  // Filter state
  const [selectedCategory, setSelectedCategory] = useState(cached.current?.selectedCategory || "");
  const [selectedCountry, setSelectedCountry] = useState(cached.current?.selectedCountry || "");
  const [selectedFundingStage, setSelectedFundingStage] = useState(cached.current?.selectedFundingStage || "");
  const [selectedType, setSelectedType] = useState<"all" | "startup" | "vendor">(cached.current?.selectedType || "all");

  // Filter
  const [filter, setFilter] = useState<FilterMode>(cached.current?.filter || "all");

  // Searched text banner (from navbar)
  const [searchedText, setSearchedText] = useState<string | null>(cached.current?.searchedText || null);

  // Source dropdown state (per card)
  const [openSources, setOpenSources] = useState<string | null>(null);
  const sourceButtonRef = useRef<HTMLButtonElement | null>(null);
  const sourceDropdownRef = useRef<HTMLDivElement | null>(null);
  const restoringRef = useRef(true);

  // Autocomplete dropdown outside-click ref
  const acWrapperRef = useRef<HTMLDivElement | null>(null);

  // Feedback state: key = `${startupName}::${query}`, value = "up" | "down"
  const [feedback, setFeedback] = useState<Record<string, "up" | "down">>({});

  const submitFeedback = async (startupName: string, rating: "up" | "down") => {
    const query = searchedText || textQuery || "";
    const key = `${startupName}::${query}`;
    setFeedback(prev => ({ ...prev, [key]: rating }));
    try {
      await axios.post(`${STARTUP_API_URL}/feedback`, {
        startup_name: startupName,
        query,
        rating,
        user_id: localStorage.getItem("uid") || null,
      });
    } catch {
      // silent — UI already updated optimistically
    }
  };

  const [visibleCount, setVisibleCount] = useState(LOAD_MORE_BATCH);

  // Scroll to top
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close sources dropdown on outside click
  useEffect(() => {
    if (openSources === null) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (
        (sourceDropdownRef.current && target && sourceDropdownRef.current.contains(target)) ||
        (sourceButtonRef.current && target && sourceButtonRef.current.contains(target))
      ) return;
      setOpenSources(null);
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenSources(null); };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [openSources]);

  // Close autocomplete suggestions on outside click or touch
  useEffect(() => {
    if (!ac.showSuggestions) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (acWrapperRef.current && !acWrapperRef.current.contains(e.target as Node)) {
        ac.clear();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [ac.showSuggestions]);

  // Load filter options once
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await axios.get(`${STARTUP_API_URL}/api/v1/startups/filters`);
        setFilterOptions(res.data.data);
      } catch {}
    };
    fetchFilters();
  }, []);

  // Restore from cache on mount
  useEffect(() => {
    setTimeout(() => { restoringRef.current = false; }, 120);
  }, []);

  // Reset visible count whenever filter changes
  useEffect(() => {
    setVisibleCount(LOAD_MORE_BATCH);
  }, [filter]);

  // Auto-fetch more results if both DB + live finish with < 6 total
  useEffect(() => {
    if (liveLoading || loading || fetchingMore || noMoreResults) return;
    if (autoMinFetchedRef.current) return;
    if (startups.length > 0 && startups.length < 6) {
      autoMinFetchedRef.current = true;
      fetchMoreLive(); // eslint-disable-line react-hooks/exhaustive-deps
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveLoading, loading, fetchingMore, noMoreResults, startups.length]);


  // Save state to session
  useEffect(() => {
    if (startups.length > 0) {
      saveState({
        startups, textQuery, selectedCategory, selectedCountry, selectedFundingStage,
        selectedType, filter, searchedText,
      });
    }
  }, [startups, selectedCategory, selectedCountry, selectedFundingStage, filter, searchedText]);

  // Handle navbar search_query (same pattern as discover page)
  useEffect(() => {
    if (location.state?.search_query) {
      const query = location.state.search_query;
      setSearchedText(query);
      setTextQuery(query);
      setSelectedCategory("");
      setSelectedCountry("");
      setSelectedFundingStage("");
      setFilter("all");

      // Pre-populate with results passed from chat (instant display before API responds)
      const prefillFromState: Startup[] = location.state?.prefill_results || [];
      const prefillFromStorage = (() => {
        try {
          const raw = sessionStorage.getItem("ucdp_prefill_startups");
          if (!raw) return [];
          const { query: pq, results } = JSON.parse(raw);
          if (pq === query) { sessionStorage.removeItem("ucdp_prefill_startups"); return results || []; }
        } catch {}
        return [];
      })();
      const prefill: Startup[] = prefillFromState.length ? prefillFromState : prefillFromStorage;
      if (prefill.length) {
        // Clear any stale session cache so old results don't re-render over the prefill
        try { sessionStorage.removeItem("startupSearchState"); } catch {}
        // Cross-check localStorage to immediately show correct badge for already-saved startups
        const savedNames: string[] = JSON.parse(localStorage.getItem("ucdp_saved_startups") || "[]");
        const savedSet = new Set(savedNames);
        setStartups(prefill.map(s => ({
          ...s,
          data_source: savedSet.has((s.name || "").toLowerCase().trim()) ? "database" : (s.data_source || "live"),
        })));
        setLoading(false);
      }

      (async () => {
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        const signal = abortRef.current.signal;

        setLoading(prefill.length === 0);
        setLiveLoading(true);
        if (prefill.length === 0) setStartups([]);
        setVisibleCount(6);
        setNoMoreResults(false);
        autoMinFetchedRef.current = false;

        // 🔥 Fire DB and Live simultaneously
        const dbPromise = axios.post(`${STARTUP_API_URL}/api/v1/startups/ai-search`, {
          query, is_db: true,
        }, { signal });

        const livePromise = axios.post(`${STARTUP_API_URL}/api/v1/startups/ai-search`, {
          query, is_db: false, exclude_names: [],
        }, { timeout: 90000, signal });

        dbPromise.then(dbRes => {
          if (!mountedRef.current) return;
          const dbData = (dbRes.data.data || []).map((s: Startup) => ({ ...s, data_source: "database" }));
          setStartups(prev => prev.some(s => s.id === null) ? smartMerge(prev, dbData) : removeDuplicatesByName([...dbData, ...prev]));
          setLoading(false);
          if (dbData.length > 0) {
            setShowSuccessAlert(true);
            setTimeout(() => setShowSuccessAlert(false), 3000);
          }
        }).catch((err: any) => {
          if (axios.isCancel(err) || err?.name === 'AbortError') return;
          if (mountedRef.current) setLoading(false);
        });

        livePromise.then(liveRes => {
          if (!mountedRef.current) return;
          const liveData = (liveRes.data.data || []).map((s: Startup) => ({ ...s, data_source: "live" }));
          setStartups(prev => prev.some(s => s.id === null) ? smartMerge(prev, liveData) : removeDuplicatesByName([...prev, ...liveData]));
          setLiveLoading(false);
          if (liveData.length > 0) {
            setShowSuccessAlert(true);
            setTimeout(() => setShowSuccessAlert(false), 3000);
          }
        }).catch((err: any) => {
          if (axios.isCancel(err) || err?.name === 'AbortError') return;
          if (err?.code === 'ECONNABORTED') {
            if (mountedRef.current) setLiveLoading(false);
            return;
          }
          if (mountedRef.current) setLiveLoading(false);
        });

        await Promise.race([dbPromise, livePromise]).catch(() => {});
      })();

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setSearchedText(null);
    setLoading(true);
    setLiveLoading(true);
    setStartups([]);
    setFilter("all");
    setVisibleCount(6);
    setNoMoreResults(false);
    autoMinFetchedRef.current = false;

    const baseQuery = textQuery.trim();
    // Steer LLM toward the right company type via query suffix
    const typeSuffix = selectedType === "vendor"
      ? "vendor solution provider service company enterprise software"
      : selectedType === "startup"
      ? "startup funded early-stage"
      : "";
    const query = baseQuery
      ? (typeSuffix ? `${baseQuery} ${typeSuffix}` : baseQuery)
      : typeSuffix || undefined;
    const category = selectedCategory || undefined;
    const country = selectedCountry || undefined;
    const funding_stage = selectedType === "vendor" ? undefined : (selectedFundingStage || undefined);

    // 🔥 Fire DB and Live searches SIMULTANEOUSLY — not sequentially
    const dbPromise = axios.post(`${STARTUP_API_URL}/api/v1/startups/ai-search`, {
      query, category, country, funding_stage, is_db: true,
    }, { signal });

    const livePromise = axios.post(`${STARTUP_API_URL}/api/v1/startups/ai-search`, {
      query, category, country, funding_stage, is_db: false,
      exclude_names: [],
    }, { timeout: 90000, signal });

    // DB results — show as soon as they arrive
    dbPromise.then(dbRes => {
      if (!mountedRef.current) return;
      const dbData = (dbRes.data.data || []).map((s: Startup) => ({ ...s, data_source: "database" }));
      setStartups(prev => removeDuplicatesByName([...dbData, ...prev]));
      setLoading(false);
      if (dbData.length > 0) {
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
      }
    }).catch((err: any) => {
      if (axios.isCancel(err) || err?.name === 'AbortError') return;
      if (mountedRef.current) setLoading(false);
    });

    // Live results — show as soon as they arrive (runs in parallel with DB)
    livePromise.then(liveRes => {
      if (!mountedRef.current) return;
      const liveData = (liveRes.data.data || []).map((s: Startup) => ({ ...s, data_source: "live" }));
      setStartups(prev => removeDuplicatesByName([...prev, ...liveData]));
      setLiveLoading(false);
      if (liveData.length > 0) {
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
      }
    }).catch((err: any) => {
      if (axios.isCancel(err) || err?.name === 'AbortError') return;
      // Timeout — silently stop, keep whatever results came in
      if (err?.code === 'ECONNABORTED') {
        if (mountedRef.current) setLiveLoading(false);
        return;
      }
      if (mountedRef.current) setLiveLoading(false);
    });

    // Wait for at least one to settle before returning control
    await Promise.race([dbPromise, livePromise]).catch(() => {});
  };

  const cancelLiveSearch = () => {
    abortRef.current?.abort();
    if (mountedRef.current) setLiveLoading(false);
  };

  const hasDescription = (s: Startup) =>
    !!(s.short_description && s.short_description !== "null" && s.short_description.trim());

  const getFilteredItems = () => {
    return startups
      .filter(s => filter === "all" ? true : filter === "database" ? s.data_source === "database" : s.data_source === "live")
      .sort((a, b) => Number(hasDescription(b)) - Number(hasDescription(a)));
  };

  // Fetch more live results from backend
  const fetchMoreLive = async () => {
    if (fetchingMore) return;
    setFetchingMore(true);
    try {
      const excludeNames = startups.map(s => s.name);
      const activeQuery = searchedText || textQuery.trim() || undefined;
      const res = await axios.post(`${STARTUP_API_URL}/api/v1/startups/ai-search`, {
        query: activeQuery,
        category: selectedCategory || undefined,
        country: selectedCountry || undefined,
        funding_stage: selectedFundingStage || undefined,
        is_db: false,
        exclude_names: excludeNames,
      }, { timeout: 180000 });
      const newData = (res.data.data || []).map((s: Startup) => ({ ...s, data_source: "live" }));
      if (mountedRef.current) {
        setStartups(prev => removeDuplicatesByName([...prev, ...newData]));
        if (newData.length === 0) setNoMoreResults(true);
      }
    } catch {}
    finally { setFetchingMore(false); }
  };

  const handleLoadMore = async () => {
    const filtered = getFilteredItems();
    if (visibleCount < filtered.length) {
      setVisibleCount(prev => prev + LOAD_MORE_BATCH);
    } else if (!noMoreResults && !fetchingMore) {
      await fetchMoreLive();
      if (mountedRef.current) setVisibleCount(prev => prev + LOAD_MORE_BATCH);
    }
  };

  const makeUID = (s: Startup, idx: number) => {
    if (s.id) return String(s.id);
    return `${s.name?.replace(/\s+/g, "_").slice(0, 40)}_${idx}`;
  };

  return (
    <div
      className="min-h-screen relative flex flex-col overflow-x-hidden startup-dir-page"
      style={{
        background: "radial-gradient(35% 19% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white",
      }}
    >
      {/* Dots bg */}
      <div
        className="absolute"
        style={{
          width: "1106px", height: "500px", top: "-12px", left: "194px",
          backgroundImage: "url('/dots.png')",
          backgroundSize: "contain", backgroundRepeat: "no-repeat",
          opacity: 1, pointerEvents: "none", userSelect: "none", zIndex: 0,
        }}
      />
      {/* Side ornaments */}
      <div
        className="hidden md:block absolute pointer-events-none select-none"
        style={{ top: "40%", left: "-24px", transform: "translateY(-50%)", width: 100, height: 250, backgroundImage: "url('./disover_rounded_rectangle.png')", backgroundRepeat: "no-repeat", backgroundSize: "contain", backgroundPosition: "center", zIndex: 0 }}
      />
      <div
        className="hidden md:block absolute pointer-events-none select-none"
        style={{ top: "40%", right: "-24px", transform: "translateY(-50%)", width: 100, height: 250, backgroundImage: "url('./discover_Circle.png')", backgroundRepeat: "no-repeat", backgroundSize: "contain", backgroundPosition: "center", zIndex: 0 }}
      />

      {/* Navbar */}
      <div className="w-full bg-transparent border-b border-white/10 shadow-sm relative z-[1000]">
        <Navbar onSearchClick={() => {}} />
      </div>

      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-12 lg:px-24 flex flex-col flex-1">
        {/* Title */}
        <div className="relative z-10 w-full flex flex-col items-center mt-30">
          <div style={{ width: "100%", display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
            <button
              onClick={() => showBackToChat ? navigate(-1) : navigate("/")}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "none", border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8, padding: "5px 12px", cursor: "pointer",
                color: "#9ca3af", fontSize: 13, fontFamily: "Sora",
                transition: "color 0.15s, border-color 0.15s",
              }}
              onMouseOver={e => { (e.currentTarget as HTMLButtonElement).style.color = "#FF541F"; (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,84,31,0.4)"; }}
              onMouseOut={e => { (e.currentTarget as HTMLButtonElement).style.color = "#9ca3af"; (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.12)"; }}
            >
              ← Back to Chat
            </button>
          </div>
          <h1
            style={{ fontFamily: "Sora", fontWeight: 700, fontSize: "20px", lineHeight: "150%", textAlign: "center", color: "#FFFFFF" }}
          >
            Startup Search
          </h1>
          {searchedText && (
            <div style={{ marginTop: 8, fontFamily: "Sora", fontWeight: 500, fontSize: "14px", color: "#D9D9D9" }}>
              Showing results for: <span style={{ color: "#FF541F" }}>{searchedText}</span>
            </div>
          )}
        </div>

        {/* Filter Card */}
        <div className="w-full flex justify-center mt-6 relative z-30">
          <div className="w-full rounded-2xl border border-white/10 bg-white/[0.03]">
            <form
              className="pl-10 pr-2 py-10 flex flex-col gap-6"
              onSubmit={handleSubmit}
            >
              {/* Text Search */}
              <div ref={acWrapperRef} className="flex flex-col gap-2 pr-8 relative">
                <label style={{ fontFamily: "Sora", fontWeight: 400, fontSize: "16px", color: "#808080", padding: "2px 8px", display: "inline-block" }}>
                  Search
                </label>
                <input
                  type="text"
                  value={textQuery}
                  onChange={e => { setTextQuery(e.target.value); ac.setShowSuggestions(true); }}
                  onKeyDown={e => ac.handleKeyDown(e, s => { setTextQuery(s); ac.clear(); triggerSearch(); }, triggerSearch)}
                  onFocus={() => { if (ac.suggestions.length > 0) ac.setShowSuggestions(true); }}
                  placeholder="Search startups by name, technology, sector..."
                  className="w-full h-14 rounded-[12px] pl-4 pr-4 bg-[#1a1a1a] border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-[#FF541F] text-sm"
                  style={{ fontFamily: "Sora" }}
                />
                {ac.showSuggestions && ac.suggestions.length > 0 && (
                  <ul className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl overflow-hidden border border-white/10 shadow-2xl" style={{ background: "#1a1a1a", color: "#ffffff" }}>
                    {ac.suggestions.map((s, i) => (
                      <li
                        key={s}
                        onMouseDown={e => { e.preventDefault(); setTextQuery(s); ac.clear(); triggerSearch(); }}
                        className="px-4 py-2.5 text-sm cursor-pointer transition-colors"
                        style={{ background: i === ac.activeSuggestion ? "rgba(255,84,31,0.15)" : "transparent", color: i === ac.activeSuggestion ? "#FF541F" : "#d1d5db" }}
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Dropdowns + Button row */}
              <div className="grid grid-cols-1 md:grid-cols-[auto_0.5rem_auto_0.5rem_auto_0.5rem_auto_auto] items-end gap-x-4">
              {/* Category */}
              <div className="flex flex-col gap-2">
                <label style={{ fontFamily: "Sora", fontWeight: 400, fontSize: "16px", color: "#808080", padding: "2px 8px", display: "inline-block" }}>
                  Category
                </label>
                <div className="h-14">
                  <select
                    value={selectedCategory}
                    onChange={e => setSelectedCategory(e.target.value)}
                    className="w-full md:w-[200px] h-14 rounded-[12px] pl-4 pr-8 bg-[#1a1a1a] border border-white/10 focus:outline-none focus:border-[#FF541F] text-sm appearance-none cursor-pointer"
                    style={{ fontFamily: "Sora", color: selectedCategory ? "#ffffff" : "#808080" }}
                  >
                    <option value="" style={{ background: "#1a1a1a", color: "#808080" }}>Select Category</option>
                    {filterOptions?.categories.map(c => (
                      <option key={c.id} value={c.name} style={{ background: "#1a1a1a", color: "#fff" }}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:flex justify-center items-end h-14">
                <div className="w-px h-full border-l" style={{ borderColor: "rgba(255,255,255,0.08)" }} />
              </div>

              {/* Country */}
              <div className="flex flex-col gap-2">
                <label style={{ fontFamily: "Sora", fontWeight: 400, fontSize: "16px", color: "#808080", padding: "2px 8px", display: "inline-block" }}>
                  Country
                </label>
                <div className="h-14">
                  <select
                    value={selectedCountry}
                    onChange={e => setSelectedCountry(e.target.value)}
                    className="w-full md:w-[200px] h-14 rounded-[12px] pl-4 pr-8 bg-[#1a1a1a] border border-white/10 focus:outline-none focus:border-[#FF541F] text-sm appearance-none cursor-pointer"
                    style={{ fontFamily: "Sora", color: selectedCountry ? "#ffffff" : "#808080" }}
                  >
                    <option value="" style={{ background: "#1a1a1a", color: "#808080" }}>Select Country</option>
                    {filterOptions?.countries.map(c => (
                      <option key={c} value={c} style={{ background: "#1a1a1a", color: "#ffffff" }}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:flex justify-center items-end h-14">
                <div className="w-px h-full border-l" style={{ borderColor: "rgba(255,255,255,0.08)" }} />
              </div>

              {/* Funding Stage */}
              <div className="flex flex-col gap-2">
                <label style={{ fontFamily: "Sora", fontWeight: 400, fontSize: "16px", color: "#808080", padding: "2px 8px", display: "inline-block" }}>
                  Funding Stage
                </label>
                <div className="h-14">
                  <select
                    value={selectedFundingStage}
                    onChange={e => setSelectedFundingStage(e.target.value)}
                    className="w-full md:w-[200px] h-14 rounded-[12px] pl-4 pr-8 bg-[#1a1a1a] border border-white/10 focus:outline-none focus:border-[#FF541F] text-sm appearance-none cursor-pointer"
                    style={{ fontFamily: "Sora", color: selectedFundingStage ? "#ffffff" : "#808080" }}
                  >
                    <option value="" style={{ background: "#1a1a1a", color: "#808080" }}>Select Stage</option>
                    {filterOptions?.funding_stages.map(s => (
                      <option key={s} value={s} style={{ background: "#1a1a1a", color: "#ffffff" }}>{formatFundingStage(s)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:flex justify-center items-end h-14">
                <div className="w-px h-full border-l" style={{ borderColor: "rgba(255,255,255,0.08)" }} />
              </div>

              {/* Type */}
              <div className="flex flex-col gap-2">
                <label style={{ fontFamily: "Sora", fontWeight: 400, fontSize: "16px", color: "#808080", padding: "2px 8px", display: "inline-block" }}>
                  Type
                </label>
                <div className="h-14">
                  <select
                    value={selectedType}
                    onChange={e => setSelectedType(e.target.value as "all" | "startup" | "vendor")}
                    className="w-full md:w-[160px] h-14 rounded-[12px] pl-4 pr-8 bg-[#1a1a1a] border border-white/10 focus:outline-none focus:border-[#FF541F] text-sm appearance-none cursor-pointer"
                    style={{ fontFamily: "Sora", color: selectedType === "all" ? "#808080" : "#ffffff" }}
                  >
                    <option value="all" style={{ background: "#1a1a1a", color: "#808080" }}>Select Type</option>
                    <option value="startup" style={{ background: "#1a1a1a", color: "#ffffff" }}>Startup</option>
                    <option value="vendor" style={{ background: "#1a1a1a", color: "#ffffff" }}>Solution Provider</option>
                  </select>
                </div>
              </div>

              {/* Search Button */}
              <div className="flex">
                <button
                  id="startup-search-btn"
                  type="submit"
                  disabled={loading || liveLoading}
                  className="w-full h-14 px-6 rounded-[12px] text-white font-semibold border-0 transition-all duration-200 disabled:opacity-60"
                  style={{ background: "#FF541F", fontFamily: "Sora" }}
                >
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              </div>{/* end dropdowns row */}
            </form>
          </div>
        </div>

        {/* Alerts */}
        {showSuccessAlert && (
          <div className="fixed top-3 left-1/2 z-50 -translate-x-1/2 w-full max-w-md">
            <Alert variant="success" close={true} onClose={() => setShowSuccessAlert(false)}>
              <AlertIcon><RiCheckboxCircleFill /></AlertIcon>
              <AlertTitle>Data received successfully!</AlertTitle>
            </Alert>
          </div>
        )}
        {showErrorAlert && (
          <div className="fixed top-3 left-1/2 z-50 -translate-x-1/2 w-full max-w-md">
            <Alert variant="destructive" close={true} onClose={() => setShowErrorAlert(false)}>
              <AlertIcon><span className="text-xl">!</span></AlertIcon>
              <AlertTitle>Failed to fetch data. Please try again.</AlertTitle>
            </Alert>
          </div>
        )}

        {/* Content */}
        <main className="relative z-0 flex flex-1 flex-col items-center justify-center">
          {/* Loader */}
          {loading && (
            <div className="w-full flex-1 flex flex-col items-center justify-center mt-36">
              <RippleWaveLoader />
              <span className="mt-4 text-gray-200 font-medium text-lg">Searching database...</span>
              <span className="mt-1 text-gray-500 text-sm">AI is verifying relevance of results</span>
              <div className="min-h-[50vh]" />
            </div>
          )}

          {/* Results */}
          {startups.length > 0 ? (
            <div className="w-full flex flex-col items-center mt-8 px-4">
              {/* Results Header */}
              <div className="w-full max-w-7xl mb-6 flex flex-col gap-3">
                <div className="w-full">
                  <span style={{ fontFamily: "Sora", fontWeight: 400, fontSize: 16, color: "#808080" }}>
                    Search Results
                  </span>
                </div>

                {/* Filter chips */}
                <div className="w-full min-h-[62px]">
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex items-center justify-center gap-3">
                      {(["all", "database", "live"] as FilterMode[]).map(f => (
                        <button
                          key={f}
                          className={`px-4 py-2 rounded-full font-semibold border shadow-sm transition-colors duration-150 ${
                            filter === f
                              ? "bg-[#FF541F] border-[#FF541F] text-white"
                              : "bg-transparent border-white/10 text-white"
                          }`}
                          onClick={() => { setFilter(f); setVisibleCount(LOAD_MORE_BATCH); }}
                        >
                          {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                      ))}
                    </div>
                    {liveLoading && (
                      <span className="text-sm text-gray-400 flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full bg-green-400 animate-pulse" />
                        Live search running...
                        <button
                          onClick={cancelLiveSearch}
                          className="ml-1 px-2 py-0.5 text-xs rounded-md border border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-all"
                        >
                          Cancel
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Cards Grid */}
              {(() => {
                const filtered = getFilteredItems();
                const visibleItems = filtered.slice(0, visibleCount);
                // Show loading spinner until we have 6 cards ready (or all loading finishes)
                const stillAccumulating = filtered.length > 0 && filtered.length < 6 && (liveLoading || fetchingMore);
                const showCards = filtered.length >= 6 || (filtered.length > 0 && !liveLoading && !fetchingMore);

                if (stillAccumulating) return (
                  <div className="col-span-1 md:col-span-3 w-full flex flex-col items-center py-12">
                    <RippleWaveLoader />
                    <span className="mt-4 text-gray-200 font-medium text-lg">Loading results...</span>
                    <span className="text-gray-500 text-sm mt-1">{filtered.length} found so far, waiting for more…</span>
                  </div>
                );

                return showCards ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-7xl">
                      {visibleItems.map((startup, idx) => {
                        const uid = makeUID(startup, idx);
                        const isOpen = openSources === uid;
                        const isTracked = trackedNames.has((startup.name || "").toLowerCase().trim());
                        const isDb = startup.data_source === "database" || isTracked;

                        return (
                          <div
                            key={uid}
                            className={`relative text-white hover:scale-[1.02] transition-transform duration-200 ${isOpen ? "z-50" : ""}`}
                            style={{
                              background: "linear-gradient(0deg, rgba(39, 40, 41, 0.7), rgba(39, 40, 41, 0.7)),linear-gradient(119.3deg, rgba(0, 0, 0, 0) 23.34%, rgba(255, 60, 0, 0.76) 99.36%)",
                              borderRadius: 20,
                              paddingTop: 12, paddingRight: 24, paddingBottom: 64, paddingLeft: 24,
                              border: "1px solid #FFFFFF1A",
                              display: "flex", flexDirection: "column", justifyContent: "space-between",
                              gap: 12,
                            }}
                          >
                            {/* Badge */}
                            <span
                              className="absolute top-2 right-2 z-10"
                              style={{
                                width: 62, height: 18,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                borderRadius: 20, padding: "1px",
                                background: "#48BB781A",
                                fontWeight: 600, fontSize: "12px",
                                color: isDb ? "#3B82F6" : "#22C55E",
                                letterSpacing: "0.01em",
                              }}
                            >
                              {isDb ? "Database" : "Live"}
                            </span>

                            <div style={{ marginTop: "2px" }} />

                            {/* Logo + Name */}
                            <div className="flex items-center gap-3 pr-16">
                              {startup.logo_url ? (
                                <img src={startup.logo_url} alt={startup.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF541F] to-[#FF3C00] flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                                  {startup.name?.charAt(0) || "?"}
                                </div>
                              )}
                              <div>
                                <h2 style={{ fontFamily: "IBM Plex Sans, sans-serif", fontWeight: 600, fontSize: "18px", lineHeight: "120%", color: "#FFFFFF" }}>
                                  {startup.name}
                                </h2>
                                {startup.category && (
                                  <span style={{ fontSize: "13px", color: "#808080", fontFamily: "Sora" }}>{startup.category.name}</span>
                                )}
                              </div>
                            </div>

                            {/* Description */}
                            {startup.short_description && startup.short_description !== "null" && (
                              <p style={{ fontFamily: "Lora, serif", fontWeight: 400, fontSize: "15px", lineHeight: "1.5", color: "#D9D9D9D9" }}>
                                {startup.short_description}
                              </p>
                            )}

                            {/* Tags */}
                            <div className="flex flex-wrap gap-2">
                              {startup.has_deployment_record && startup.data_source !== "live" && (
                                <span className="px-2.5 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
                                  <FiCheckCircle className="w-3 h-3" /> Verified
                                </span>
                              )}
                              {startup.customer_engagements != null && startup.customer_engagements > 0 && startup.data_source !== "live" && (
                                <span className="px-2.5 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 flex items-center gap-1">
                                  <FiUsers className="w-3 h-3" /> {startup.customer_engagements}+ Customers
                                </span>
                              )}
                              {startup.funding_stage && startup.funding_stage !== "unknown" && (
                                <span className="px-2.5 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                                  {formatFundingStage(startup.funding_stage)}
                                </span>
                              )}
                              {(() => {
                                const loc = (startup.region?.country && startup.region.country !== "null" ? startup.region.country : null)
                                  || (startup.headquarters && startup.headquarters !== "null" ? startup.headquarters.split(",").pop()?.trim() : null);
                                return loc ? (
                                  <span className="px-2.5 py-1 rounded-full text-xs bg-white/10 text-gray-300 flex items-center gap-1">
                                    <FiGlobe className="w-3 h-3" />
                                    {loc}
                                  </span>
                                ) : null;
                              })()}
                              {startup.total_funding > 0 && (
                                <span className="px-2.5 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
                                  <FiDollarSign className="w-3 h-3" /> {formatFunding(startup.total_funding)}
                                </span>
                              )}
                              {startup.founded_year && (
                                <span className="px-2.5 py-1 rounded-full text-xs bg-white/10 text-gray-300">
                                  Est. {startup.founded_year}
                                </span>
                              )}
                            </div>

                            {/* Website / Source button */}
                            <div className="flex items-center gap-3 mb-2">
                              {startup.website ? (
                                <a
                                  href={startup.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  className="flex items-center gap-2 font-semibold text-xs rounded-lg px-3 py-2 transition-colors"
                                  style={{ gap: 8, background: "#2A2A2A", color: "#F3F4F6", textDecoration: "none" }}
                                >
                                  <FiExternalLink className="w-3.5 h-3.5" />
                                  Website
                                </a>
                              ) : (
                                <a
                                  href={`https://www.google.com/search?q=${encodeURIComponent(startup.name + " official website")}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  className="flex items-center gap-2 font-semibold text-xs rounded-lg px-3 py-2 transition-colors"
                                  style={{ gap: 8, background: "#1A1A1A", color: "#9CA3AF", textDecoration: "none", border: "1px solid #333" }}
                                >
                                  <FiExternalLink className="w-3.5 h-3.5" />
                                  Find Website
                                </a>
                              )}
                              {(() => {
                                // Filter to only valid HTTP URLs
                                const validSources = (startup.source_urls || []).filter(s => {
                                  try { const u = new URL(s); return u.protocol === "http:" || u.protocol === "https:"; } catch { return false; }
                                });
                                if (isDb || validSources.length === 0) return null;
                                return (
                                  <div className="relative inline-block">
                                    <button
                                      ref={isOpen ? sourceButtonRef : null}
                                      type="button"
                                      className="flex items-center font-semibold text-xs rounded-lg px-3 py-2 transition-colors"
                                      style={{ gap: 8, background: "#2A2A2A", color: "#F3F4F6", border: "none" }}
                                      onClick={() => setOpenSources(isOpen ? null : uid)}
                                    >
                                      <span>Sources</span>
                                      <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                    </button>
                                    {isOpen && (
                                      <div
                                        ref={sourceDropdownRef}
                                        className="absolute left-0 mt-2 w-[min(420px,90vw)] z-50"
                                        style={{ background: "rgba(18,18,20,0.95)", border: "1px solid rgba(67,72,84,0.6)", borderRadius: 12, padding: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.5)", backdropFilter: "blur(12px)" }}
                                      >
                                        <ul className="flex flex-col gap-2 m-0 p-0">
                                          {validSources.slice(0, 3).map((src, sidx) => {
                                            let domain = "";
                                            try { domain = new URL(src).hostname.replace(/^www\./i, ""); } catch {}
                                            return (
                                              <li key={sidx}>
                                                <a href={src} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition border border-white/5 hover:border-orange-500/30 group"
                                                  style={{ textDecoration: "none" }}
                                                >
                                                  <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} alt={domain} style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0 }} />
                                                  <div style={{ minWidth: 0, flex: 1 }}>
                                                    <div style={{ color: "#ffffff", fontSize: "0.85rem", fontFamily: "Sora", fontWeight: 500 }} className="group-hover:text-orange-400 transition-colors truncate">{domain}</div>
                                                    <div style={{ color: "#737373", fontSize: "11px", fontFamily: "Sora", wordBreak: "break-all", lineHeight: 1.4 }}>{src}</div>
                                                  </div>
                                                </a>
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>

                            {/* Feedback buttons */}
                            {(() => {
                              const fKey = `${startup.name}::${searchedText || textQuery || ""}`;
                              const given = feedback[fKey];
                              return (
                                <div className="flex items-center gap-2 mb-1">
                                  <button
                                    onClick={e => { e.stopPropagation(); submitFeedback(startup.name, "up"); }}
                                    title="Relevant result"
                                    style={{
                                      background: given === "up" ? "rgba(34,197,94,0.2)" : "rgba(255,255,255,0.06)",
                                      border: given === "up" ? "1px solid rgba(34,197,94,0.5)" : "1px solid rgba(255,255,255,0.1)",
                                      borderRadius: 8, padding: "5px 10px", cursor: "pointer",
                                      color: given === "up" ? "#22C55E" : "#9CA3AF",
                                      fontSize: 16, lineHeight: 1, transition: "all 0.15s",
                                    }}
                                  >👍</button>
                                  <button
                                    onClick={e => { e.stopPropagation(); submitFeedback(startup.name, "down"); }}
                                    title="Not relevant"
                                    style={{
                                      background: given === "down" ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.06)",
                                      border: given === "down" ? "1px solid rgba(239,68,68,0.5)" : "1px solid rgba(255,255,255,0.1)",
                                      borderRadius: 8, padding: "5px 10px", cursor: "pointer",
                                      color: given === "down" ? "#EF4444" : "#9CA3AF",
                                      fontSize: 16, lineHeight: 1, transition: "all 0.15s",
                                    }}
                                  >👎</button>
                                  {given && (
                                    <span style={{ fontSize: 11, color: "#6B7280" }}>
                                      {given === "up" ? "Marked relevant" : "Marked not relevant"}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Track This Startup / Already Tracking */}
                            {trackedNames.has((startup.name || "").toLowerCase().trim()) ? (
                              <button
                                onClick={e => { e.stopPropagation(); navigate('/engagement-repo'); }}
                                className="flex items-center gap-1.5 font-semibold text-xs rounded-lg px-3 py-2 mb-3 transition-colors"
                                style={{ background: "rgba(34,197,94,0.12)", color: "#4ade80", border: "1px solid rgba(34,197,94,0.3)" }}
                                title="Already tracking — click to open Engagement Repo"
                              >
                                <FiCheckCircle className="w-3.5 h-3.5" />
                                Already Tracking
                              </button>
                            ) : (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  navigate('/engagement-repo', {
                                    state: {
                                      prefill: {
                                        company_name: startup.name || "",
                                        sector: mapCategoryToSector(startup.category?.name || ""),
                                        technology_area: [...new Set(startup.tags || [])].join(", "),
                                        website: startup.website || "",
                                        geography: startup.region?.country || startup.headquarters || "",
                                        startup_stage: (() => {
                                          const fs = (startup.funding_stage || "").toLowerCase();
                                          if (["pre-seed", "pre_seed", "seed", "angel", "grant", "bootstrapped"].some(s => fs.includes(s))) return "Early";
                                          if (["series_a", "series a", "series_b", "series b"].some(s => fs.includes(s))) return "Growth";
                                          if (["series_c", "series c", "series_d", "series d", "series_e", "late", "ipo", "public"].some(s => fs.includes(s))) return "Scale";
                                          return "";
                                        })(),
                                        founders: startup.founders?.join(", ") || "",
                                        notes: startup.short_description && startup.short_description !== "null"
                                          ? startup.short_description
                                          : "",
                                      }
                                    }
                                  });
                                }}
                                className="flex items-center gap-1.5 font-semibold text-xs rounded-lg px-3 py-2 mb-3 transition-colors"
                                style={{ background: "rgba(99,102,241,0.15)", color: "#818CF8", border: "1px solid rgba(99,102,241,0.3)" }}
                                title="Track this startup in Engagement Repo"
                              >
                                <FiBookmark className="w-3.5 h-3.5" />
                                Track this startup
                              </button>
                            )}

                            {/* View Details Button */}
                            <div style={{ position: "absolute", bottom: 20, right: 24 }}>
                              <button
                                className="flex items-center justify-center"
                                style={{
                                  width: 48, height: 48, borderRadius: "50%",
                                  background: "linear-gradient(119.3deg, #FF541F 23.34%, #FF3C00 96.36%)",
                                  boxShadow: "0 2px 8px rgba(255,60,0,0.15)",
                                  border: "none", cursor: "pointer", transition: "transform 0.15s",
                                }}
                                onClick={() => navigate(`/startup/${startup.id || "live"}`, { state: { startup, search_query: textQuery } })}
                                aria-label="View Details"
                              >
                                <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                                  <path d="M7 17L17 7M17 7H9M17 7V15" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Loading placeholder when fetching first live batch */}
                      {(fetchingMore || liveLoading) && visibleItems.length === 0 && (
                        <div className="col-span-1 md:col-span-3 w-full flex flex-col items-center py-8">
                          <RippleWaveLoader />
                          <span className="mt-4 text-gray-200 font-medium text-lg">Loading more startups...</span>
                        </div>
                      )}
                    </div>

                    {/* Results count + Load More */}
                    <div className="flex flex-col items-center mt-8 gap-3">
                      <div className="text-gray-400 text-sm">
                        Showing {Math.min(visibleCount, filtered.length)} of {filtered.length} results
                        {liveLoading && <span className="ml-2 text-green-400">· Live search running...</span>}
                      </div>

                      {(visibleCount < filtered.length || (!noMoreResults && !liveLoading)) ? (
                        noMoreResults && visibleCount >= filtered.length ? (
                          <div className="flex items-center gap-3 px-6 py-3 rounded-full" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="#555" strokeWidth="2" />
                              <path d="M8 12h8" stroke="#555" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            <span style={{ fontFamily: "Sora", fontSize: 13, color: "#666" }}>No more results</span>
                          </div>
                        ) : (
                          <button
                            onClick={handleLoadMore}
                            disabled={fetchingMore}
                            className="px-8 py-3 rounded-full font-semibold text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50"
                            style={{
                              background: "linear-gradient(119.3deg, #FF541F 23.34%, #FF3C00 96.36%)",
                              boxShadow: "0 4px 16px rgba(255, 60, 0, 0.25)",
                              fontFamily: "Sora",
                            }}
                          >
                            {fetchingMore ? "Loading..." : "Load More Results"}
                          </button>
                        )
                      ) : null}
                    </div>
                  </>
                ) : (
                  <div className="text-center text-gray-500 text-xl font-semibold py-12">No startups match this filter</div>
                );
              })()}
            </div>
          ) : (
            <div className="w-full flex-1 flex flex-col items-center justify-center">
              <div className="text-center text-gray-500 text-2xl font-semibold py-12">
                {loading ? null : liveLoading ? (
                  <div className="flex flex-col items-center gap-4">
                    <RippleWaveLoader />
                    <span className="text-gray-300 text-lg">Searching web for startups...</span>
                    <span className="text-gray-500 text-sm">No database results — fetching live data</span>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-green-400 text-sm font-medium">Live AI search running</span>
                    </div>
                    <button
                      onClick={cancelLiveSearch}
                      className="mt-2 px-4 py-1.5 text-sm rounded-lg border border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-all"
                    >
                      Cancel Search
                    </button>
                  </div>
                ) : "Select filters and search to discover startups"}
              </div>
              <div className="min-h-[50vh]" />
            </div>
          )}
        </main>
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-2 z-50 bg-white text-neutral-500 rounded-full shadow-lg w-12 h-12 flex items-center justify-center transition hover:bg-neutral-50 border-2 border-neutral-500 group"
          aria-label="Scroll to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 group-hover:-translate-y-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}

      <div className="mt-12 relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default StartupSearch;
